import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Where to point your provider" — the prototype's dialog (`num.pointOpen`, design export of 2026-10-10), opened
 * from the ghost button beside the view switcher. It replaced the page's edge card: WeTalk's SIP host, its
 * address, the ports and the audio range, each with Copy, and Done.
 *
 * The values are the API's (`readSipTrunkSettings`), never the prototype's example. One sentence of the
 * prototype's copy is narrowed: it says the sign-in details "for each number were shown once when you connected
 * it", and WeTalk's inbound sign-in details belong to a TRUNK and are shown once when they are CREATED (a trunk's
 * "Create sign-in details"; a registration trunk needs none) — so the sentence says that. The two states the
 * edge card used to carry stay, because they are true and change what the member should do: the SIP edge not
 * answering, and receiving through your own trunk not switched on yet.
 */
import { Button, Dialog } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import type { SipTrunkSettingsView } from "../../api/slice/sip-trunk.js";

type CopyState = "copied" | "refused";

export interface PointProviderDialogProps {
  readonly settings: SipTrunkSettingsView | undefined;
  readonly onClose: () => void;
}

/** The four rows the prototype draws (`trunk.edge`): what a provider types in. */
export function edgeRowsOf(settings: SipTrunkSettingsView): readonly (readonly [string, string])[] {
  return [
    [portalText("finance", "merge.numbers.sipHost"), settings.sipEdgeHost],
    [portalText("finance", "merge.numbers.address"), settings.sipEdgeAddress],
    [portalText("finance", "merge.numbers.ports"), `UDP ${String(settings.port.udp)} · TCP ${String(settings.port.tcp)} · TLS ${String(settings.port.tls)}`],
    [portalText("finance", "merge.numbers.audio"), `UDP ${String(settings.rtpPortRange.start)}–${String(settings.rtpPortRange.end)}`],
  ];
}

export function PointProviderDialog({ settings, onClose }: PointProviderDialogProps): ReactNode {
  usePortalTranslation("finance");
  const [copied, setCopied] = useState<Readonly<Record<string, CopyState>>>({});
  const copy = (label: string, text: string): void => {
    const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
    const done = (state: CopyState): void => {
      setCopied((was) => ({ ...was, [label]: state }));
    };
    if (clipboard === undefined) {
      done("refused");
      return;
    }
    clipboard.writeText(text).then(
      () => {
        done("copied");
      },
      () => {
        done("refused");
      },
    );
  };

  return (
    <Dialog
      open
      title={portalText("finance", "merge.numbers.pointProvider")}
      onClose={onClose}
      actions={
        <Button variant="primary" onClick={onClose}>{portalText("finance", "merge.numbers.done")}</Button>
      }
    >
      <p className="nb-dialog-lede">{portalText("finance", "merge.numbers.providerLede")}</p>
      {settings === undefined ? (
        <p className="c-note">{portalText("finance", "merge.numbers.readingSip")}</p>
      ) : (
        <div className="nb-edge-list">
          {edgeRowsOf(settings).map(([label, value]) => (
            <div key={label} className="nb-edge-row">
              <span className="nb-edge-text">
                <span className="nb-edge-label">{label}</span>
                <code className="nb-edge-value">{value}</code>
              </span>
              <Button
                variant="secondary"
                aria-label={portalText("finance", "merge.numbers.copyLabel", { label })}
                onClick={() => {
                  copy(value, value);
                }}
              >
                {copied[value] === "copied" ? portalText("finance", "merge.numbers.copied") : portalText("finance", "merge.numbers.copy")}
              </Button>
            </div>
          ))}
        </div>
      )}
      {Object.values(copied).includes("refused") ? (
        <p className="c-note c-note-critical" role="alert">{portalText("finance", "merge.numbers.copyRefused")}</p>
      ) : null}
      {settings === undefined || settings.sipEdgeReachable ? null : (
        <p className="c-note c-note-critical" role="alert">{portalText("finance", "merge.numbers.edgeDown")}</p>
      )}
      {settings === undefined || settings.inbound === "on" ? null : (
        <p className="c-note">{portalText("finance", "merge.numbers.inboundOff")}</p>
      )}
    </Dialog>
  );
}
